/**
 * useDocumentTitle — put the client's own name in the browser tab.
 *
 * The SPA ships one static `<title>` in index.html, which meant every install
 * — demo, Rangoli, the jewellery build — showed the developer's project name
 * to whoever was sitting in front of it. In a shared screen or a projected
 * demo the tab is the first thing a client reads, so it has to say who they
 * are, not who built it.
 *
 * The static title stays as the product name for the pre-auth screens (login,
 * password reset), where no company is resolved yet. Once the session knows
 * which company it is presenting, that name takes over.
 */
import { useEffect } from 'react';
import { useCurrentCompany } from '@/shared/auth/use-current-company';

/** Shown before a company resolves, and if the lookup fails. */
const FALLBACK = 'Omeecron ERP';

export function useDocumentTitle(): void {
  const { company } = useCurrentCompany();

  useEffect(() => {
    const name = company?.name?.trim();
    // Suffix rather than replace: with several installs open at once the tab
    // still has to be identifiable as this product, and a company name alone
    // ("Demo Company") would not be.
    document.title = name ? `${name} · ${FALLBACK}` : FALLBACK;
  }, [company?.name]);
}
